import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, RefreshCw, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, X, FileText, Printer, GitCompare } from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import OrderDetails from '../Orders/OrderDetails';

const TrackByVoucher = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [voucherNo, setVoucherNo] = useState('');
  const [vouchers, setVouchers] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');

  useEffect(() => {
    initializeData();
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (warning) {
      const timer = setTimeout(() => setWarning(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [warning]);

  const initializeData = () => {
    setVoucherNo('');
    setVouchers([]);
    setCurrentPage(0);
    setCurrentOrder(null);
    setIsLoading(false);
  };

  const handleTrack = async (overrideVoucherNo) => {
    // Button onClick passes a SyntheticEvent — only treat real string/number overrides as input.
    const raw =
      typeof overrideVoucherNo === 'string' || typeof overrideVoucherNo === 'number'
        ? overrideVoucherNo
        : voucherNo;
    const inputValue = String(raw ?? '').trim();
    if (!inputValue || inputValue === '[object Object]') {
      setWarning('Please enter voucher numbers');
      return;
    }

    const voucherNumbers = inputValue
      .split('.')
      .map((v) => v.trim())
      .filter((v) => v !== '');

    if (voucherNumbers.length === 0) {
      setWarning('Please enter valid voucher numbers');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      setWarning('');

      const response = await MainService.TrackOrdersByVoucherNo({
        VoucherNos: voucherNumbers,
      });

      if (response && response.length > 0) {
        setVouchers(response);
        setCurrentPage(0);
        setCurrentOrder(response[0]);
        setSuccess(`Found ${response.length} order(s)`);
      } else {
        setVouchers([]);
        setCurrentOrder(null);
        setWarning('No orders found');
      }
    } catch (err) {
      console.error('Error tracking orders:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-track when opened from top-menu Quick Track (?voucher=...)
  useEffect(() => {
    const voucher = searchParams.get('voucher');
    if (!voucher?.trim()) return;
    setVoucherNo(voucher);
    void handleTrack(voucher);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleTrack();
    }
  };

  const handleNext = () => {
    if (currentPage < vouchers.length - 1) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      setCurrentOrder(vouchers[newPage]);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      setCurrentOrder(vouchers[newPage]);
    }
  };

  const handlePrint = async () => {
    if (!currentOrder?.Id) {
      setError('Print error: Order ID not found');
      return;
    }

    try {
      setIsPrinting(true);
      setError('');
      const response = await MainService.PrintOrder(currentOrder.Id);
      const filePath = typeof response === 'string' ? response : (response?.data || response);

      if (filePath) {
        const fullUrl = String(filePath).startsWith('http')
          ? filePath
          : `${getApiBaseUrl()}ReportFiles/${filePath}`;
        window.open(fullUrl, '_blank');
      } else {
        setError('Print error');
      }
    } catch (err) {
      console.error('Print error:', err);
      setError('Print error');
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="fixed top-20 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-20 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {warning && (
        <div className="fixed top-20 right-4 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{warning}</span>
          <button onClick={() => setWarning('')} className="text-yellow-500 hover:text-yellow-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center mb-2">
            <FileText className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">Track By Voucher</h1>
          </div>
          <p className="text-sm text-gray-500 ml-8">Enter voucher numbers to track your orders</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Voucher Numbers
            </label>
            <input
              type="text"
              value={voucherNo}
              onChange={(e) => setVoucherNo(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter Voucher Numbers Ex: 101.150.554.6658 etc"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div className="flex items-end gap-3">
            <button
              onClick={() => handleTrack()}
              disabled={isLoading}
              className="bg-primary hover:bg-primary/90 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
            >
              <Search className="w-4 h-4" />
              <span>Track</span>
            </button>
            <button
              onClick={initializeData}
              className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {vouchers.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Order {currentPage + 1} of {vouchers.length}
                {currentOrder?.VoucherNo ? (
                  <span className="ml-2 text-base font-normal text-gray-500">
                    ({currentOrder.VoucherNo}
                    {currentOrder.StoreRefNo != null && currentOrder.StoreRefNo !== ''
                      ? ` · Store Ref: ${currentOrder.StoreRefNo}`
                      : ''}
                    )
                  </span>
                ) : null}
              </h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrevious}
                  disabled={currentPage === 0}
                  className="bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>
                <button
                  onClick={handleNext}
                  disabled={currentPage === vouchers.length - 1}
                  className="bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => currentOrder?.Id && navigate(`/home/orders/${currentOrder.Id}/history-comparison`)}
                  disabled={!currentOrder?.Id || isLoading}
                  className="border border-[#46955A] text-[#46955A] hover:bg-[#f0f9f4] disabled:opacity-50 disabled:cursor-not-allowed font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2"
                >
                  <GitCompare className="w-4 h-4" />
                  <span>History Comparison</span>
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  disabled={!currentOrder?.Id || isPrinting || isLoading}
                  className="bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2"
                >
                  <Printer className={`w-4 h-4 ${isPrinting ? 'animate-pulse' : ''}`} />
                  <span>{isPrinting ? 'Printing...' : 'Print'}</span>
                </button>
              </div>
            </div>
          </div>

          {currentOrder?.Id ? (
            <div className="p-2 sm:p-4">
              <OrderDetails
                key={currentOrder.Id}
                orderId={String(currentOrder.Id)}
                embedded
              />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-blue-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Track Your Orders</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Enter voucher numbers separated by dots (e.g., 101.150.554.6658) to track your orders and view detailed information.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <h4 className="font-medium text-gray-900 mb-2">How to track:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Enter one or more voucher numbers</li>
                  <li>• Separate multiple numbers with dots</li>
                  <li>• Click Track or press Enter</li>
                  <li>• View full order details with all tabs</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <h4 className="font-medium text-gray-900 mb-2">What you'll see:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Order info, status & history</li>
                  <li>• History comparison (field differences)</li>
                  <li>• Pickup, delivery & shipping</li>
                  <li>• Payments, returns & attachments</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackByVoucher;
